'use client';
import { MotionMaybe, useMotionControls } from '@/utils/animation';
import { getCTA } from '@/utils/getCTA';
import { getColorScheme } from '@/utils/getColorScheme';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { useMemo, useState } from 'react';
import { MarkDown } from './atoms/MarkDown';

function normalizeAnimation(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'fade',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'fade';
}

function mapGroup(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    if (
        [
            'features',
            'feature',
            'features-only',
            'feature-only',
            'badge',
            'badges',
            'badge-only',
            'badges-only',
            'logos',
            'logo',
        ].includes(v)
    )
        return 'features';
    if (
        ['cta', 'ctas', 'cta-only', 'ctas-only', 'buttons', 'button'].includes(
            v
        )
    )
        return 'ctas';
    return 'content';
}

function getGridColumnsClass(numBadges, mobileColumns) {
    if (numBadges === 1) return 'grid-cols-1';
    if (numBadges === 2) return 'grid-cols-2';
    if ([4, 7, 8].includes(numBadges)) return 'grid-cols-4';
    if ([3, 5, 6, 9].includes(numBadges))
        return `grid-cols-${mobileColumns} md:grid-cols-3`;
    return '';
}

function isRemoteUrl(url) {
    return url.startsWith('http');
}

function makeLocalUrlRefImages(url) {
    return url.startsWith('/images') ? url : '/images/' + url.split('/').pop();
}

export default function Credentials(props) {
    const {
        Tagline,
        Header,
        Body,
        Badges = [],
        CTA,
        CustomCTALink,
        SecondaryButtonText,
        SecondaryButtonLink,
        OutlineButtonText,
        OutlineButtonLink,
        ColorScheme = 'secondary',
        VerticalSpacing = 'medium',
        Layout = 'Default',
        Animation = 'none',
        Duration = 0.6,
        AnimationGroup = 'features',
        EditorPreview = false,
    } = props;

    const {
        backgroundColor,
        textColor,
        headerColor,
        badgeColor,
        secondaryButton,
        thirdButton,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);

    const vSpace = getVerticalSpacing(VerticalSpacing);

    const numBadges = Math.min(Badges.length, 9);
    const mobileColumns = 2;
    const normalizedLayout = String(Layout || 'Default')
        .toLowerCase()
        .trim();
    const isTwoColumnLayout = [
        'two-column',
        'two column',
        'twocolumn',
    ].includes(normalizedLayout);
    const [hoveredBadge, setHoveredBadge] = useState(null);
    const hasBadges = numBadges > 0;
    const hasAnyCTA = Boolean(CTA || SecondaryButtonText || OutlineButtonText);

    const preset = normalizeAnimation(Animation);
    const requestedGroup = mapGroup(AnimationGroup);
    const group =
        requestedGroup === 'features' && !hasBadges
            ? 'content'
            : requestedGroup === 'ctas' && !hasAnyCTA
            ? 'content'
            : requestedGroup;
    const m = useMotionControls({
        preset,
        duration: Number(Duration) || 0.6,
        editorPreview: EditorPreview,
        group,
        keyBase: 'credentials',
    });
    const badgeChildMotionProps = useMemo(() => {
        if (!m.enableAnim) return m.childMotionProps;
        if (preset !== 'stagger' || group !== 'features') {
            return m.childMotionProps;
        }
        return {
            variants: {
                hidden: { opacity: 0, y: 12 },
                show: {
                    opacity: 1,
                    y: 0,
                    transition: {
                        duration: Math.max(0.3, Number(Duration) - 0.1 || 0.2),
                    },
                },
            },
        };
    }, [m.childMotionProps, m.enableAnim, preset, group, Duration]);

    const ctaAnimGroup = 'ctas';
    const buttonAlign = isTwoColumnLayout
        ? 'justify-center lg:justify-start'
        : 'justify-center';

    const { hasAny: hasCTAs, element: CTAs } = getCTA(
        {
            ...props,
            CTA,
            CustomCTALink,
            SecondaryButtonText,
            SecondaryButtonLink,
            OutlineButtonText,
            OutlineButtonLink,
        },
        {
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign,
            enableAnim: m.enableChild(ctaAnimGroup),
            motionProps: m.childMotionProps,
        }
    );

    return (
        <section
            className={`editable-component relative ${backgroundColor} ${vSpace} px-6 lg:px-8`}
            style={backgroundStyle}
            data-json="credentials"
        >
            {isTwoColumnLayout ? (
                <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-x-8 gap-y-12 lg:grid-cols-2">
                    <div className="mx-auto w-full max-w-xl text-center lg:mx-0 lg:text-left">
                        <MotionMaybe
                            enable={m.enableContainer('content')}
                            nodeKey={`${m.remountKey}---text`}
                            motionProps={m.containerMotionProps}
                            className=""
                        >
                            {Tagline && (
                                <MotionMaybe
                                    enable={m.enableChild('content')}
                                    nodeKey={`${m.remountKey}---tagline`}
                                    motionProps={m.childMotionProps}
                                >
                                    <MarkDown
                                        className={`mb-3 max-w-2xl text-base font-semibold uppercase leading-7 tracking-wide ${headerColor}`}
                                    >
                                        {Tagline}
                                    </MarkDown>
                                </MotionMaybe>
                            )}
                            <MotionMaybe
                                enable={m.enableChild('content')}
                                nodeKey={`${m.remountKey}---header`}
                                motionProps={m.childMotionProps}
                            >
                                <MarkDown
                                    variant="h2"
                                    className={`font-display text-3xl tracking-tight ${headerColor} lg:text-4xl`}
                                >
                                    {Header}
                                </MarkDown>
                            </MotionMaybe>
                            <MotionMaybe
                                enable={m.enableChild('content')}
                                nodeKey={`${m.remountKey}---body`}
                                motionProps={m.childMotionProps}
                            >
                                <MarkDown
                                    className={`mt-6 text-lg font-text leading-8 ${textColor}`}
                                >
                                    {Body}
                                </MarkDown>
                            </MotionMaybe>
                            {hasCTAs && (
                                <MotionMaybe
                                    enable={m.enableContainer(ctaAnimGroup)}
                                    nodeKey={`${m.remountKey}---ctas`}
                                    motionProps={m.containerMotionProps}
                                    className={`mt-8 flex flex-wrap items-center ${buttonAlign} gap-4`}
                                >
                                    <MotionMaybe
                                        enable={m.enableChild(ctaAnimGroup)}
                                        nodeKey={`${m.remountKey}---ctas-child`}
                                        motionProps={m.childMotionProps}
                                        className="flex flex-wrap items-center gap-4"
                                    >
                                        {CTAs}
                                    </MotionMaybe>
                                </MotionMaybe>
                            )}
                        </MotionMaybe>
                    </div>

                    <MotionMaybe
                        enable={m.enableContainer('features')}
                        nodeKey={`${m.remountKey}---badges`}
                        motionProps={m.containerMotionProps}
                        className={`mx-auto grid w-full max-w-xl ${getGridColumnsClass(
                            numBadges,
                            mobileColumns
                        )} items-center gap-12 sm:gap-y-14 lg:mx-0 lg:max-w-none lg:pl-8 ${badgeColor}`}
                    >
                        {Badges.slice(0, 9).map((Badge, index) => {
                            const badgeKey = `${m.remountKey}---badge-${index}`;
                            const imageElement = (
                                <img
                                    className={`${
                                        numBadges === 1
                                            ? 'max-h-40 lg:max-h-52'
                                            : 'max-h-32 lg:max-h-40'
                                    } w-full object-contain object-center`}
                                    src={
                                        isRemoteUrl(Badge.Image)
                                            ? Badge.Image
                                            : makeLocalUrlRefImages(Badge.Image)
                                    }
                                    alt={Badge.AltText}
                                    width={128}
                                    height={64}
                                    loading="lazy"
                                    style={{
                                        filter:
                                            hoveredBadge === index && Badge.Link
                                                ? 'brightness(0.7)'
                                                : 'none',
                                        transition: 'filter 0.3s ease-in-out',
                                    }}
                                />
                            );

                            const badgeNode = Badge.Link ? (
                                <a
                                    href={Badge.Link}
                                    onMouseEnter={() => setHoveredBadge(index)}
                                    onMouseLeave={() => setHoveredBadge(null)}
                                    className="block w-full"
                                >
                                    {imageElement}
                                </a>
                            ) : (
                                imageElement
                            );

                            return (
                                <MotionMaybe
                                    key={badgeKey}
                                    nodeKey={badgeKey}
                                    enable={m.enableChild('features')}
                                    motionProps={badgeChildMotionProps}
                                    className="flex w-full justify-center"
                                >
                                    {badgeNode}
                                </MotionMaybe>
                            );
                        })}
                    </MotionMaybe>
                </div>
            ) : (
                <div className="mx-auto max-w-7xl">
                    <div className="mx-auto mb-6 w-full text-center md:mb-10 lg:mx-0">
                        <MotionMaybe
                            enable={m.enableContainer('content')}
                            nodeKey={`${m.remountKey}---text`}
                            motionProps={m.containerMotionProps}
                            className=""
                        >
                            {Tagline && (
                                <MotionMaybe
                                    enable={m.enableChild('content')}
                                    nodeKey={`${m.remountKey}---tagline`}
                                    motionProps={m.childMotionProps}
                                >
                                    <MarkDown
                                        className={`mx-auto mb-3 max-w-2xl text-base font-semibold uppercase leading-7 tracking-wide ${headerColor}`}
                                    >
                                        {Tagline}
                                    </MarkDown>
                                </MotionMaybe>
                            )}
                            <MotionMaybe
                                enable={m.enableChild('content')}
                                nodeKey={`${m.remountKey}---header`}
                                motionProps={m.childMotionProps}
                            >
                                <MarkDown
                                    variant="h2"
                                    className={`mx-auto max-w-2xl font-display text-3xl tracking-tight lg:max-w-4xl ${headerColor} lg:text-4xl`}
                                >
                                    {Header}
                                </MarkDown>
                            </MotionMaybe>
                            <MotionMaybe
                                enable={m.enableChild('content')}
                                nodeKey={`${m.remountKey}---body`}
                                motionProps={m.childMotionProps}
                            >
                                <MarkDown
                                    className={`mx-auto mt-6 max-w-2xl text-lg font-text leading-8 lg:max-w-4xl ${textColor}`}
                                >
                                    {Body}
                                </MarkDown>
                            </MotionMaybe>
                        </MotionMaybe>
                    </div>

                    <MotionMaybe
                        enable={m.enableContainer('features')}
                        nodeKey={`${m.remountKey}---badges`}
                        motionProps={m.containerMotionProps}
                        className={`mx-auto flex flex-wrap items-center justify-center gap-8 lg:gap-x-24 ${badgeColor}`}
                    >
                        {Badges.slice(0, 9).map((Badge, index) => {
                            const badgeKey = `${m.remountKey}---badge-${index}`;
                            const imageElement = (
                                <img
                                    className="max-h-28 max-w-48 object-contain lg:object-center"
                                    src={
                                        isRemoteUrl(Badge.Image)
                                            ? Badge.Image
                                            : makeLocalUrlRefImages(Badge.Image)
                                    }
                                    alt={Badge.AltText}
                                    width={192}
                                    height={112}
                                    loading="lazy"
                                    style={{
                                        maxWidth: '192px',
                                        maxHeight: '112px',
                                        filter:
                                            hoveredBadge === index && Badge.Link
                                                ? 'brightness(0.7)'
                                                : 'none',
                                        transition: 'filter 0.3s ease-in-out',
                                    }}
                                />
                            );

                            const badgeNode = Badge.Link ? (
                                <a
                                    href={Badge.Link}
                                    onMouseEnter={() => setHoveredBadge(index)}
                                    onMouseLeave={() => setHoveredBadge(null)}
                                    className="inline-flex"
                                >
                                    {imageElement}
                                </a>
                            ) : (
                                imageElement
                            );

                            return (
                                <MotionMaybe
                                    key={badgeKey}
                                    nodeKey={badgeKey}
                                    enable={m.enableChild('features')}
                                    motionProps={badgeChildMotionProps}
                                    className="inline-flex justify-center"
                                >
                                    {badgeNode}
                                </MotionMaybe>
                            );
                        })}
                    </MotionMaybe>
                    {hasCTAs && CTAs}
                </div>
            )}
        </section>
    );
}
